import React, { useState } from "react";
import { Link } from "react-router-dom";
import Pagination from "../common/Pagination";
import { SortArrow, SortAsc, SortDesc } from "../common/ProjectIcons";
import TextInput from "../common/TextInput";
import { Organization, Video, VideoCataloguesDto } from "../common/ObjectTypes";
import tailwindClassnames from "../common/TaildwindClassnames";

type VideoCataloguesListProps = {
  videoResults: VideoCataloguesDto[];
  sortVideosName: Function;
  sortVideosDate: Function;
  sortVideosPublished: Function;
  onChangeFilter: Function;
  onSearch: Function;
  onSelectVideo: Function;
  subStr: string;
  pageCount: number;
  pageSize: number;
  totalCount: number;
  handlePageClick: (selectedItem: { selected: number }) => void;
  reloadPage: boolean;
  recordStart: number;
  recordEnd: number;
  selectVideoCataloguesPage: boolean;
  onAddCatalogue: Function;
  photoUrl: string;
  organizations: Organization[];
  searchQuery?: any;
};

const VideoCataloguesList = ({
  videoResults,
  sortVideosName,
  searchQuery,
  sortVideosDate,
  sortVideosPublished,
  onChangeFilter,
  onSearch,
  onSelectVideo,
  subStr,
  pageCount,
  pageSize,
  totalCount,
  handlePageClick,
  reloadPage,
  recordStart,
  recordEnd,
  selectVideoCataloguesPage,
  onAddCatalogue,
  photoUrl,
  organizations,
}: VideoCataloguesListProps) => {
  const [videoOrderByName, setVideoOrderByName] = useState(false);
  function formatDateTime(dateTimeStr: string) {
    const date = new Date(dateTimeStr);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    let formattedDate = date.toLocaleString("en-US", options);
    formattedDate = formattedDate.replace(" AM", "am").replace(" PM", "pm");
    return formattedDate;
  }

  const getOrgById = (orgId: any) => {
    let orgObj = organizations?.find((org) => org.organizationId === orgId);
    return orgObj?.name || "";
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      {!selectVideoCataloguesPage && (
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-800">
            Video Catalogues
          </h1>
        </div>
      )}

      {/* Search + Add Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 w-full max-w-md">
          <TextInput
            name=""
            label=""
            onChange={onChangeFilter}
            value={subStr}
            placeholder="Filter by name"
            error={null}
            disabled={false}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-tealBlue-400 focus:ring focus:ring-tealBlue-200"
            type="text"
            onKeyPress={(e) => {
              if (e.key === "Enter") onSearch();
            }}
            initialFocus={false}
          />
          <button
            onClick={() => onSearch()}
            className="px-4 py-2 rounded-full text-sm font-medium text-white bg-tealBlue-500 hover:bg-tealBlue-600 shadow transition"
          >
            Search
          </button>
        </div>

        {!selectVideoCataloguesPage && (
          <Link
            to="/videoCatalogue/add"
            className="px-4 py-2 rounded-full text-sm font-medium text-white bg-tealBlue-500 hover:bg-tealBlue-600 shadow transition"
          >
            + Add
          </Link>
        )}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full border-collapse text-sm text-gray-700">
          <thead className="bg-gray-50 border-b">
            <tr>
              {selectVideoCataloguesPage && (
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Select
                </th>
              )}
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                <div className="flex items-center gap-1">
                  Name
                  <button
                    onClick={() => {
                      setVideoOrderByName(!videoOrderByName);
                      sortVideosName("name", !videoOrderByName);
                    }}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <SortArrow />
                  </button>
                </div>
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Organization
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Description
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Last Edited Date
              </th>
            </tr>
          </thead>

          <tbody>
            {videoResults.map(
              (videoCatalogue: VideoCataloguesDto, index: number) => {
                const rowClass = index % 2 === 0 ? "bg-white" : "bg-gray-50";

                return (
                  <tr
                    key={videoCatalogue.videoCatalogueId}
                    className={`${rowClass} hover:bg-gray-100 transition cursor-pointer`}
                    onClick={() =>
                      !selectVideoCataloguesPage &&
                      onSelectVideo(videoCatalogue.videoCatalogueId)
                    }
                  >
                    {selectVideoCataloguesPage && (
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-tealBlue-500 rounded border-gray-300 focus:ring-tealBlue-400"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {videoCatalogue.name}
                    </td>
                    <td className="px-4 py-3 w-48">
                      {getOrgById(videoCatalogue.organizationId)}
                    </td>
                    <td className="px-4 py-3 w-1/2">
                      <p className="text-gray-600 line-clamp-2">
                        {videoCatalogue.description.substring(0, 200)}
                        {videoCatalogue.description.length > 200 ? "..." : ""}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {formatDateTime(videoCatalogue.lastEditedDate)}
                    </td>
                  </tr>
                );
              }
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        loading={reloadPage}
        handlePageClick={handlePageClick}
        pageCount={pageCount}
        recordStart={recordStart}
        recordEnd={recordEnd}
        totalCount={totalCount}
        pageSize={searchQuery.pageSize}
        searchQuery={searchQuery}
        selectPage={selectVideoCataloguesPage}
      />
    </div>
  );
};

export default VideoCataloguesList;
